import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useMe } from '@/api/auth';
import { ApiError, setUnauthorizedHandler } from '@/api/client';
import { getToken } from '@/lib/authToken';

/**
 * Gate every authenticated page on a valid session.
 * - No token in localStorage → redirect to /login
 * - Token exists but /auth/me answers 401/403 → token cleared (by
 *   `apiFetch` for 401), redirect to /login.
 * - /auth/me unreachable (connection error, 5xx while the backend
 *   restarts) → NOT a session problem (FH-009): keep the token, render
 *   the app shell with its per-query degraded states, show a retrying
 *   banner, and re-check every few seconds.
 * - /auth/me succeeds → render children.
 *
 * `errorUpdateCount` latches "the check has failed at least once": retry
 * cycles put the query back into pending, and gating on bare pending
 * would unmount the shell (and its useMe observers) on every retry —
 * the oscillation this guard exists to avoid. The full-screen spinner
 * is therefore reserved for the first-ever check.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hasToken = Boolean(getToken());
  const { data: me, isPending, isError, error, errorUpdateCount, refetch } = useMe();

  // 401/403 = the backend heard us and said no. Anything else that
  // errors — fetch rejection, 502 from the proxy, 500 — means the
  // backend didn't answer properly and proves nothing about the session.
  const authRejected =
    isError && error instanceof ApiError && (error.status === 401 || error.status === 403);
  const hasFailedBefore = errorUpdateCount > 0;
  const backendUnreachable =
    !authRejected && (isError || (isPending && hasFailedBefore));

  // Wire up the global 401-handler so any future API call that
  // returns 401 also bounces us to /login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      window.location.assign('/login?m=Session+expired');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // While the backend is unreachable, keep probing so recovery is
  // automatic — no manual reload needed.
  useEffect(() => {
    if (!backendUnreachable) return;
    const id = setInterval(() => refetch(), 5000);
    return () => clearInterval(id);
  }, [backendUnreachable, refetch]);

  if (!hasToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (isPending && !hasFailedBefore) {
    return (
      <div className="min-h-screen flex items-center justify-center text-fg-muted text-sm">
        <Loader2 size={14} className="animate-spin mr-2" /> Verifying session…
      </div>
    );
  }

  if (authRejected || (!isPending && !isError && (!me || me.status !== 'active'))) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      {backendUnreachable && (
        <div className="sticky top-0 z-[100] flex items-center gap-2 px-4 py-1.5 text-xs border-b border-warn/40 bg-[color:var(--warn-soft)]">
          <AlertTriangle size={12} className="text-warn shrink-0" />
          <span>
            Backend unreachable — your session is intact; retrying every few seconds.
          </span>
        </div>
      )}
      {children}
    </>
  );
}